import { useEffect, useMemo, useState } from 'react';
import { LuDownload } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import MonthPicker from '../components/MonthPicker';
import { ListRow } from '../components/ListKit';
import {
    MensagensSistemaService,
    type AtendimentoResposta,
    type ConsumoResposta,
} from '../services/MensagensSistemaService';
import './MensagensSistemaPage.css';

/**
 * Mensagens — área Sistema (só GB Code).
 *
 * Duas abas, porque são duas perguntas com fontes de verdade diferentes:
 * **Consumo** é a conta, e o eixo é o NÚMERO (é o número que a Meta cobra e é por
 * número que a cota de 1.000 mensagens de serviço corre); **Atendimento** é a
 * autoria, e o eixo é a EMPRESA (é assim que se fala de cliente).
 *
 * docs/designsystem/propostas/admin/fase-5-mensagens.html (Direção B).
 */

type Aba = 'consumo' | 'atendimento';

const CHAVE_ABA = 'sis-mensagens-aba';
const CHAVE_MES = 'sis-mensagens-mes';

/* Quem absorve a sobra é a PRIMEIRA coluna, que é texto e cresce sem estranheza.
   Com o `1fr` na última, em tela larga o custo ia parar na borda direita e abria
   um vão de centenas de pixels entre ele e "Faturáveis" — os números precisam
   ficar juntos para serem comparáveis. */
const COLUNAS_CONSUMO = 'minmax(280px, 1fr) 120px 116px 200px 110px 110px 122px';
const COLUNAS_ATENDIMENTO = 'minmax(240px, 1fr) 100px 100px 330px 100px 120px';

const mesCorrente = () => {
    const hoje = new Date();
    return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
};

/** Primeiro e último dia do mês, em ISO — o recorte que a cota da Meta usa. */
const limitesDoMes = (mes: string) => {
    const [ano, m] = mes.split('-').map(Number);
    const primeiro = `${mes}-01`;
    const ultimo = new Date(ano, m, 0).getDate();
    return { de: primeiro, ate: `${mes}-${String(ultimo).padStart(2, '0')}` };
};

const numero = (v: number) => v.toLocaleString('pt-BR');
const dinheiro = (v: number, moeda: string) =>
    `${moeda === 'USD' ? 'US$' : moeda} ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const percentual = (v: number) => `${v}%`;

const dataCurta = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

const MensagensSistemaPage = () => {
    // O recorte de trabalho não pode se perder ao sair e voltar.
    const [aba, setAba] = useState<Aba>(() => (sessionStorage.getItem(CHAVE_ABA) as Aba) || 'consumo');
    const [mes, setMes] = useState(() => sessionStorage.getItem(CHAVE_MES) || mesCorrente());

    const [consumo, setConsumo] = useState<ConsumoResposta | null>(null);
    const [atendimento, setAtendimento] = useState<AtendimentoResposta | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => { sessionStorage.setItem(CHAVE_ABA, aba); }, [aba]);
    useEffect(() => { sessionStorage.setItem(CHAVE_MES, mes); }, [mes]);

    useEffect(() => {
        let cancelado = false;
        const { de, ate } = limitesDoMes(mes);

        setCarregando(true);
        setErro(null);

        const promessa = aba === 'consumo'
            ? MensagensSistemaService.consumo(de, ate).then(d => { if (!cancelado) setConsumo(d); })
            : MensagensSistemaService.atendimento(de, ate).then(d => { if (!cancelado) setAtendimento(d); });

        promessa
            .catch(e => { if (!cancelado) setErro(e instanceof Error ? e.message : 'Não foi possível carregar.'); })
            .finally(() => { if (!cancelado) setCarregando(false); });

        return () => { cancelado = true; };
    }, [aba, mes]);

    const dados = aba === 'consumo' ? consumo : atendimento;
    const medindoDesde = dados?.medindoDesde ?? null;

    const vazio = useMemo(() => {
        if (carregando || erro) return false;
        if (aba === 'consumo') return (consumo?.numeros.length ?? 0) === 0;
        return (atendimento?.empresas.length ?? 0) === 0;
    }, [aba, carregando, erro, consumo, atendimento]);

    const exportar = () => {
        const linhas: string[][] = aba === 'consumo'
            ? [
                ['Número', 'Empresa', 'Marketing', 'Utilidade', 'Serviço', 'Cota', 'Acima da cota', 'Autenticação', 'Faturáveis', 'Custo estimado'],
                ...(consumo?.numeros ?? []).map(n => [
                    n.numero, n.empresaNome, String(n.marketing), String(n.utilidade), String(n.servicoTotal),
                    String(n.cotaDoPeriodo), String(n.servicoAcimaDaCota), String(n.autenticacao),
                    String(n.faturaveis), n.custoEstimado.toFixed(2),
                ]),
            ]
            : [
                ['Empresa', 'Entraram', 'Saíram', 'Robô', 'Vendedor no chat', 'Vendedor pelo celular', 'Campanha', 'Automação', '% robô'],
                ...(atendimento?.empresas ?? []).map(e => [
                    e.empresaNome, String(e.entraram), String(e.sairam), String(e.robo), String(e.vendedorChat),
                    String(e.vendedorCelular), String(e.campanha), String(e.automacao), String(e.percentualRobo),
                ]),
            ];

        // Ponto e vírgula porque o Excel em pt-BR usa vírgula como decimal.
        const csv = linhas.map(l => l.map(c => `"${c.replace(/"/g, '""')}"`).join(';')).join('\n');
        const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `mensagens-${aba}-${mes}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="page-shell lk-screen sis-mensagens">
            <PageHeader
                title="Mensagens"
                subtitle="Consumo e atendimento de todas as empresas"
                actions={
                    <div className="sis-acoes">
                        <MonthPicker value={mes} onChange={setMes} />
                        <button type="button" className="sis-btn" onClick={exportar} disabled={vazio || carregando}>
                            <LuDownload size={15} />
                            <span>Exportar</span>
                        </button>
                    </div>
                }
                tabs={[
                    { id: 'consumo', label: 'Consumo' },
                    { id: 'atendimento', label: 'Atendimento' },
                ]}
                activeTab={aba}
                onTabChange={id => setAba(id as Aba)}
            />

            <div className="page-body">
                {erro && <div className="sis-aviso sis-aviso-erro">{erro}</div>}

                {aba === 'consumo'
                    ? <AbaConsumo dados={consumo} carregando={carregando} vazio={vazio} medindoDesde={medindoDesde} />
                    : <AbaAtendimento dados={atendimento} carregando={carregando} vazio={vazio} medindoDesde={medindoDesde} />}
            </div>
        </div>
    );
};

/** Bloco de número do topo. */
const Bloco = ({ rotulo, valor, apoio, alerta }: {
    rotulo: string; valor: string; apoio?: string; alerta?: boolean;
}) => (
    <div className={`sis-tile ${alerta ? 'is-alerta' : ''}`}>
        <div className="sis-tile-k">{rotulo}</div>
        <div className="sis-tile-v">{valor}</div>
        {apoio && <div className="sis-tile-d">{apoio}</div>}
    </div>
);

/**
 * O estado vazio diz DESDE QUANDO existe medição. Sem isso a tela parece quebrada
 * nos primeiros dias, e "zero mensagens" se confunde com "ainda não começamos a medir".
 */
const Vazio = ({ medindoDesde }: { medindoDesde: string | null }) => (
    <div className="sis-vazio">
        <p className="sis-vazio-t">Nada a mostrar neste período.</p>
        <p className="sis-vazio-d">
            {medindoDesde
                ? `A medição de mensagens começou em ${dataCurta(medindoDesde)}. Períodos anteriores a essa data não têm dado — e não terão.`
                : 'A medição de mensagens ainda não produziu nenhum dia fechado. O resumo diário roda uma vez por dia; volte amanhã.'}
        </p>
    </div>
);

const AbaConsumo = ({ dados, carregando, vazio, medindoDesde }: {
    dados: ConsumoResposta | null; carregando: boolean; vazio: boolean; medindoDesde: string | null;
}) => {
    const moeda = dados?.moeda ?? 'USD';
    const t = dados?.totais;

    return (
        <>
            <div className="sis-tiles">
                <Bloco
                    rotulo="Faturáveis no mês"
                    valor={carregando ? '—' : numero(t?.faturaveis ?? 0)}
                    apoio={t?.faturaveisProjetados ? `projeção até o fim do mês: ${numero(t.faturaveisProjetados)}` : undefined}
                />
                <Bloco
                    rotulo="Custo estimado"
                    valor={carregando ? '—' : dinheiro(t?.custoEstimado ?? 0, moeda)}
                    apoio={t?.custoProjetado ? `projeção: ${dinheiro(t.custoProjetado, moeda)}` : undefined}
                />
                {/* Enquanto a mensagem de serviço for grátis, "acima da cota" não existe —
                    alarmar sobre ela seria inventar um problema. O bloco troca de assunto em
                    vez de mostrar zero sem explicação. */}
                {dados?.servicoCobrado ? (
                    <Bloco
                        rotulo="Serviço acima da cota"
                        valor={carregando ? '—' : numero(t?.servicoAcimaDaCota ?? 0)}
                        apoio={`em ${numero(t?.numerosAcimaDaCota ?? 0)} ${(t?.numerosAcimaDaCota ?? 0) === 1 ? 'número' : 'números'}`}
                        alerta={(t?.servicoAcimaDaCota ?? 0) > 0}
                    />
                ) : (
                    <Bloco
                        rotulo="Mensagem de serviço"
                        valor="grátis"
                        apoio={dados?.servicoPassaACobrarEm
                            ? `passa a custar em ${dataCurta(dados.servicoPassaACobrarEm)}`
                            : 'sem cobrança neste período'}
                    />
                )}
                <Bloco
                    rotulo={dados?.servicoCobrado ? 'Cota grátis por número' : 'Cota a partir da virada'}
                    valor={dados?.cotaMensalPorNumero ? `${numero(dados.cotaMensalPorNumero)}/mês` : '—'}
                    apoio="mensagem de serviço, sem acúmulo"
                />
            </div>

            {/* O número é nosso, não da Meta. Dizer isso na tela não é rodapé: é o que
                impede alguém de levar esta soma para uma conversa de cobrança. */}
            <p className="sis-nota">
                O custo é <strong>estimativa</strong>: contagem faturável × tabela de tarifas
                {dados?.tarifaVigenteEm ? ` vigente desde ${dataCurta(dados.tarifaVigenteEm)}` : ''}.
                A fatura verdadeira está no Gerenciador de Negócios da Meta.
            </p>

            {vazio ? <Vazio medindoDesde={medindoDesde} /> : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS_CONSUMO }}>
                            <div className="lk-head">Número</div>
                            <div className="lk-head sis-r">Marketing</div>
                            <div className="lk-head sis-r">Utilidade</div>
                            <div className="lk-head sis-cota-h">{dados?.servicoCobrado ? 'Serviço · cota' : 'Serviço'}</div>
                            <div className="lk-head sis-r">Autenticação</div>
                            <div className="lk-head sis-r">Faturáveis</div>
                            <div className="lk-head sis-r">Custo est.</div>
                        </div>
                        <div className="lk-body">
                            {carregando
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : (dados?.numeros ?? []).map(n => {
                                    const estourou = dados!.servicoCobrado && n.servicoAcimaDaCota > 0;
                                    const proporcao = n.cotaDoPeriodo > 0
                                        ? Math.min(100, Math.round((n.servicoNaCota / n.cotaDoPeriodo) * 100))
                                        : 0;
                                    return (
                                        <ListRow key={`${n.numero}-${n.empresaId}`} columns={COLUNAS_CONSUMO}>
                                            <span className="lk-cell">
                                                <span className="sis-nome">{n.numero}</span>
                                                <span className="sis-sub">· {n.empresaNome}</span>
                                            </span>
                                            <span className="lk-cell sis-r">{n.marketing ? numero(n.marketing) : <i className="sis-zero">0</i>}</span>
                                            <span className="lk-cell sis-r">{n.utilidade ? numero(n.utilidade) : <i className="sis-zero">0</i>}</span>
                                            <span className={`lk-cell sis-cota ${dados!.servicoCobrado ? '' : 'is-sem-cota'}`}>
                                                {/* Sem cobrança não há cota, e desenhar o trilho
                                                    vazio afirmaria um limite que ainda não existe. */}
                                                {dados!.servicoCobrado && (
                                                    <span className="sis-trilho">
                                                        <i className={estourou ? 'is-estourou' : ''} style={{ width: `${estourou ? 100 : proporcao}%` }} />
                                                    </span>
                                                )}
                                                <span className="sis-cota-n">
                                                    {estourou
                                                        ? `${numero(n.cotaDoPeriodo)} + ${numero(n.servicoAcimaDaCota)}`
                                                        : numero(n.servicoTotal)}
                                                </span>
                                            </span>
                                            <span className="lk-cell sis-r">{n.autenticacao ? numero(n.autenticacao) : <i className="sis-zero">0</i>}</span>
                                            <span className="lk-cell sis-r">{n.faturaveis ? numero(n.faturaveis) : <i className="sis-zero">0</i>}</span>
                                            <span className="lk-cell sis-r">
                                                {n.custoEstimado > 0 ? dinheiro(n.custoEstimado, moeda) : <i className="sis-zero">—</i>}
                                            </span>
                                        </ListRow>
                                    );
                                })}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const AbaAtendimento = ({ dados, carregando, vazio, medindoDesde }: {
    dados: AtendimentoResposta | null; carregando: boolean; vazio: boolean; medindoDesde: string | null;
}) => {
    const t = dados?.totais;
    const pct = (parte: number, todo: number) => (todo === 0 ? 0 : Math.round((parte / todo) * 100));

    return (
        <>
            <div className="sis-tiles">
                <Bloco rotulo="Saíram" valor={carregando ? '—' : numero(t?.sairam ?? 0)}
                    apoio={`para ${numero(t?.entraram ?? 0)} que entraram`} />
                <Bloco rotulo="Robô" valor={carregando ? '—' : percentual(pct(t?.robo ?? 0, t?.sairam ?? 0))}
                    apoio={`${numero(t?.robo ?? 0)} respostas`} />
                <Bloco rotulo="Pessoa" valor={carregando ? '—' : percentual(pct(t?.pessoa ?? 0, t?.sairam ?? 0))}
                    apoio={`${numero(t?.pessoa ?? 0)} — sendo ${numero(t?.peloCelular ?? 0)} pelo celular`} />
                <Bloco rotulo="Campanha" valor={carregando ? '—' : numero(t?.campanha ?? 0)}
                    apoio="disparos de template" />
            </div>

            <div className="sis-legenda">
                <span><i className="sis-sw" style={{ background: '#235193' }} />Robô</span>
                <span><i className="sis-sw" style={{ background: '#658dc9' }} />Chat</span>
                <span><i className="sis-sw" style={{ background: '#a2c0eb' }} />Celular</span>
                <span><i className="sis-sw" style={{ background: '#dfecff' }} />Campanha</span>
            </div>

            {vazio ? <Vazio medindoDesde={medindoDesde} /> : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS_ATENDIMENTO }}>
                            <div className="lk-head">Empresa</div>
                            <div className="lk-head sis-r">Entraram</div>
                            <div className="lk-head sis-r">Saíram</div>
                            <div className="lk-head sis-barra-h">Quem respondeu</div>
                            <div className="lk-head sis-r">Robô</div>
                            <div className="lk-head sis-r">Pelo celular</div>
                        </div>
                        <div className="lk-body">
                            {carregando
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : (dados?.empresas ?? []).map(e => {
                                    const total = e.sairam || 1;
                                    const seg = [
                                        { v: e.robo, cor: '#235193' },
                                        { v: e.vendedorChat, cor: '#658dc9' },
                                        { v: e.vendedorCelular, cor: '#a2c0eb' },
                                        { v: e.campanha + e.automacao, cor: '#dfecff' },
                                    ];
                                    return (
                                        <ListRow key={e.empresaId} columns={COLUNAS_ATENDIMENTO}>
                                            <span className="lk-cell sis-nome">{e.empresaNome}</span>
                                            <span className="lk-cell sis-r">{numero(e.entraram)}</span>
                                            <span className="lk-cell sis-r">{numero(e.sairam)}</span>
                                            <span className="lk-cell sis-barra">
                                                <span className="sis-empilhada">
                                                    {seg.map((s, i) => s.v > 0 && (
                                                        <i key={i} style={{ width: `${(s.v / total) * 100}%`, background: s.cor }} />
                                                    ))}
                                                </span>
                                            </span>
                                            <span className={`lk-cell sis-r ${e.percentualRobo < 40 ? 'sis-atencao' : ''}`}>
                                                {percentual(e.percentualRobo)}
                                            </span>
                                            <span className="lk-cell sis-r">
                                                {e.vendedorCelular ? numero(e.vendedorCelular) : <i className="sis-zero">0</i>}
                                            </span>
                                        </ListRow>
                                    );
                                })}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MensagensSistemaPage;
